import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileText, Folder, User, ListTodo, ExternalLink } from "lucide-react";

const SOURCES = [
  { value: "", label: "Todos" },
  { value: "client", label: "Clientes" },
  { value: "task", label: "Tarefas" },
];

function isImage(ct = "") {
  return /image\//i.test(ct);
}

export default function Files() {
  const { apiCall } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [source, setSource] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (source) params.set("source", source);
      const res = await apiCall("get", `/attachments/all${params.toString() ? `?${params.toString()}` : ""}`);
      setItems(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, source]);

  return (
    <div className="space-y-4" data-testid="files-page">
      <div>
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Galeria</div>
        <h1 className="text-2xl font-light tracking-tight">Arquivos</h1>
        <p className="text-xs text-muted-foreground mt-1">
          Todos os anexos do salão — fotos de clientes, antes/depois, documentos de tarefas.
        </p>
      </div>

      <Card className="p-3 flex flex-col sm:flex-row gap-2">
        <div className="flex-1">
          <Input
            placeholder="Buscar por nome, descrição ou cliente/tarefa…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="files-search"
          />
        </div>
        <div className="flex gap-1">
          {SOURCES.map((s) => (
            <Button
              key={s.value || "all"}
              size="sm"
              variant={source === s.value ? "default" : "ghost"}
              onClick={() => setSource(s.value)}
              data-testid={`files-source-${s.value || "all"}`}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card className="p-6 text-center text-muted-foreground">Carregando…</Card>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center" data-testid="files-empty">
          <Folder className="h-10 w-10 mx-auto text-muted-foreground mb-2" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">Nenhum anexo encontrado.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="files-grid">
          {items.map((it) => {
            const SourceIcon = it.source === "client" ? User : ListTodo;
            return (
              <a
                key={`${it.source}-${it.attachment_id}`}
                href={it.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group border border-border rounded-md overflow-hidden hover:shadow-md transition bg-card"
                data-testid={`file-card-${it.attachment_id}`}
              >
                {isImage(it.content_type) ? (
                  <img
                    src={it.url}
                    alt={it.filename}
                    className="w-full h-32 object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-full h-32 flex flex-col items-center justify-center bg-secondary/40">
                    <FileText className="h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
                    <span className="text-[10px] mt-1 text-muted-foreground px-1 truncate w-full text-center">
                      {it.filename}
                    </span>
                  </div>
                )}
                <div className="p-2">
                  <div className="text-xs font-medium truncate" title={it.label || it.filename}>
                    {it.label || it.filename}
                  </div>
                  <div className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1 truncate">
                    <SourceIcon className="h-3 w-3 shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{it.owner_name || "—"}</span>
                  </div>
                  <div className="text-[10px] text-muted-foreground/70 mt-1 flex items-center gap-1">
                    <ExternalLink className="h-2.5 w-2.5" strokeWidth={1.5} />
                    abrir
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
